import { Button, Checkbox, List, ListItem, makeStyles, Toolbar } from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { BaseLayout, Header, Loader, NoData } from "../components";
import MeetingListItem from "../components/MeetingListItem";
import { types } from "../ducks";
import { deleteMeeting, getProfileMeetingSlots, updateMeetingSlot } from "../ducks/actions";
import { useActionRequest, useQueryParams } from "../hooks";
import useUserMe from "../hooks/useUserMe";
import { ReactComponent as MeetingSchedSvg } from "../assets/icons/meeting sched.svg";

const useStyles = makeStyles(theme => ({
  root: {
    "& > *": {
      marginBottom: theme.spacing(3)
    }
  },
  filter: {
    marginLeft: theme.spacing(1),
    flexGrow: 0.2
  },
  meetingSlotRow: {
    backgroundColor: "#cdcdcd",
    borderRadius: "0.5rem",
    margin: "0.25rem 0",
    "& > *": {
      marginRight: "1rem"
    }
  },
  dayButtonBar: {
    overflowX: "auto",
    marginBottom: 0
  },
  dayButton: {
    margin: "0 4px",
    padding: "8px 48px"
  }
}));

const MeetingSchedulerPage = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user } = useUserMe();
  const [selectedMeetingSlots, setSelectedMeetingSlots] = useState([]);

  useEffect(() => {
    if (user.profile?.id) {
      dispatch(getProfileMeetingSlots({ profileId: user.profile?.id, onlyAvailable: false }));
      setSelectedMeetingSlots([]);
    }
  }, [dispatch, user.profile?.id]);

  const {
    data: { data: meetingSlots },
    loading: meetingSlotsLoading
  } = useQuery({
    type: types.GET_PROFILE_MEETING_SLOTS,
    defaultData: { data: [] },
    autoReset: true
  });

  const onSelectedMeetingSlot =
    ({ meetingSlot }) =>
    e => {
      if (e.target.checked) {
        setSelectedMeetingSlots([...selectedMeetingSlots, meetingSlot]);
      } else {
        setSelectedMeetingSlots(selectedMeetingSlots.filter(({ id }) => id !== meetingSlot.id));
      }
    };
  const deleteMeetingRequest = useActionRequest({
    action: deleteMeeting
  });

  const onDeleteMeeting =
    ({ id }) =>
    () => {
      deleteMeetingRequest({ id });
    };

  const meetingSlotsByDay =
    _.chain(meetingSlots)
      .groupBy(({ startAt }) => moment(startAt).format("DD/MM"))
      .value() || {};

  const [{ day }, mergeParams] = useQueryParams();

  const onDaySelect =
    ({ selectedDay }) =>
    () => {
      mergeParams({ day: selectedDay });
      setSelectedMeetingSlots([]);
    };

  const currentDay = day || Object.keys(meetingSlotsByDay)[0];

  const updateMeetingSlotRequest = useActionRequest({
    action: updateMeetingSlot,
    successMessage: false
  });

  const onSelectedMeetingSlotChange = available => () => {
    selectedMeetingSlots
      .filter(meetingSlot => {
        return (
          moment(meetingSlot.startAt).isAfter(moment()) &&
          !meetingSlot.meeting &&
          meetingSlot.available !== available
        );
      })
      .forEach(meetingSlot =>
        updateMeetingSlotRequest({ id: meetingSlot.id, meetingSlot: { available } })
      );

    setSelectedMeetingSlots([]);
  };

  const onSelectAllMeetingSlot = e => {
    if (e.target.checked) {
      setSelectedMeetingSlots(
        meetingSlotsByDay[currentDay].filter(meetingSlot => {
          return moment(meetingSlot.startAt).isAfter(moment()) && !meetingSlot.meeting;
        })
      );
    } else setSelectedMeetingSlots([]);
  };

  return (
    <BaseLayout className={classes.root}>
      <Header
        icon={MeetingSchedSvg}
        title={intl.formatMessage({
          id: "Pages.MeetingSchedulerPage.Title",
          defaultMessage: "Meeting Scheduler"
        })}
      />
      <Loader loading={meetingSlotsLoading}>
        <NoData data={meetingSlots}>
          <Toolbar className={classes.dayButtonBar}>
            {Object.keys(meetingSlotsByDay).map(d => (
              <Button
                key={d}
                variant={d === currentDay ? "contained" : "text"}
                className={classes.dayButton}
                onClick={onDaySelect({ selectedDay: d })}
                color="primary"
              >
                {d}
              </Button>
            ))}
          </Toolbar>
          <List>
            <ListItem>
              <Checkbox
                checked={!_.isEmpty(selectedMeetingSlots)}
                onClick={onSelectAllMeetingSlot}
                color="primary"
              />
              <Button
                color="primary"
                variant="outlined"
                disabled={!selectedMeetingSlots.length}
                onClick={onSelectedMeetingSlotChange(true)}
              >
                {intl.formatMessage({
                  id: "Pages.MeetingSchedulerPage.SetAvailable",
                  defaultMessage: "Set as Available"
                })}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                disabled={!selectedMeetingSlots.length}
                onClick={onSelectedMeetingSlotChange(false)}
              >
                {intl.formatMessage({
                  id: "Pages.MeetingSchedulerPage.SetNotAvailable",
                  defaultMessage: "Set as Not Available"
                })}
              </Button>
            </ListItem>
            {meetingSlotsByDay[currentDay]?.map(meetingSlot => (
              <MeetingListItem
                meetingSlot={meetingSlot}
                meeting={meetingSlot?.meeting}
                profile={meetingSlot?.meeting?.profile}
                selected={selectedMeetingSlots.includes(meetingSlot)}
                onDelete={onDeleteMeeting}
                onSelect={onSelectedMeetingSlot}
              />
            ))}
          </List>
        </NoData>
      </Loader>
    </BaseLayout>
  );
};
export default MeetingSchedulerPage;
